import { useEffect, useMemo, useState } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Crosshair, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api, getApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useMapData } from "@/hooks/useMapData";
import { ActionModal } from "@/components/ActionModal";
import { MapDetailsPanel } from "@/components/MapDetailsPanel";
import { MapUserTable } from "@/components/MapUserTable";
import { OperationsMap } from "@/components/OperationsMap";
import { ErrorState, LoadingState } from "@/components/PageStates";
import { PageHeader } from "@/components/PageHeader";
import { matchesMapFilters } from "@/utils/mapData";

const FILTERS = ["all", "live", "creator", "user"];
const PLATFORMS = ["all", "ios", "android", "web", "unknown"];
const BAN_FIELDS = [{ name: "reason", label: "Ban reason", placeholder: "Explain why this account is being banned", required: true }];
const NO_FIELDS = [];
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_LIBRARIES = ["marker"];

export default function MapView() {
  const { user: adminUser } = useAuth();
  const { points, users, stats, source, loading, error, updatedAt, reload } = useMapData();
  const [filter, setFilter] = useState("all"); const [platform, setPlatform] = useState("all"); const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false); const [fitNonce, setFitNonce] = useState(0);
  const canAct = adminUser?.role === "super_admin";
  const filteredPoints = useMemo(() => points.filter((point) => matchesMapFilters(point, filter, platform, query)), [points, filter, platform, query]);
  const filteredUsers = useMemo(() => users.filter((user) => matchesMapFilters(user, filter, platform, query)), [users, filter, platform, query]);
  const missingLocations = Math.max(0, stats.total - stats.withCoordsCount);

  useEffect(() => {
    if (selected && !filteredPoints.some((point) => point.key === selected.key)) setSelected(null);
  }, [filteredPoints, selected]);

  const runAction = async (values) => {
    if (!canAct || !action?.point) return;
    setBusy(true);
    try {
      if (action.type === "ban") await api.post(`/admin/users/${action.point.userId}/ban`, values);
      if (action.type === "unban") await api.post(`/admin/users/${action.point.userId}/unban`);
      if (action.type === "force-end") await api.post(`/admin/live/${action.point.liveId}/force-end`);
      toast.success(action.type === "force-end" ? "Live session ended" : `User ${action.type === "ban" ? "banned" : "unbanned"}`);
      setAction(null); setSelected(null); reload();
    } catch (requestError) { toast.error(getApiError(requestError)); }
    finally { setBusy(false); }
  };

  const modalCopy = action?.type === "force-end" ? ["Force end live session", "The broadcast will stop immediately.", "End broadcast"]
    : action?.type === "ban" ? ["Ban user", "This account will lose access to AuraLive.", "Ban user"]
      : ["Restore user access", "This account will regain access to AuraLive.", "Unban user"];

  return (
    <div className="page map-page">
      <PageHeader eyebrow="Global operations" title="Community map" description="A complete, untruncated view of AuraLive users, creators, platforms, and live activity.">
        <button className="button secondary" onClick={() => setFitNonce((value) => value + 1)} data-testid="fit-map-markers-button"><Crosshair size={16} />Fit markers</button>
        <button className="button secondary" onClick={() => reload()} disabled={loading} data-testid="refresh-map-button"><RefreshCw size={16} className={loading ? "spin" : ""} />Refresh</button>
      </PageHeader>

      <section className="map-summary" data-testid="map-summary">
        <div><strong data-testid="map-live-count">{stats.liveCount.toLocaleString()}</strong><span>Live now</span></div>
        <div><strong data-testid="map-creator-count">{stats.creatorCount.toLocaleString()}</strong><span>Creators</span></div>
        <div><strong data-testid="map-total-count">{stats.withCoordsCount.toLocaleString()}</strong><span>Mapped locations</span></div>
        <div><strong data-testid="map-missing-count">{missingLocations.toLocaleString()}</strong><span>Without coordinates</span></div>
        <small data-testid="map-last-updated">{updatedAt ? `${stats.total.toLocaleString()} users loaded · ${updatedAt.toLocaleTimeString()}` : "Waiting for production data"}</small>
      </section>

      <section className="map-toolbar" aria-label="Map filters">
        <div className="map-filter-groups">
          <div className="map-filter-tabs" aria-label="Account type filters">{FILTERS.map((item) => <button key={item} className={filter === item ? "active" : ""} onClick={() => setFilter(item)} data-testid={`map-filter-${item}-button`}>{item === "all" ? "All locations" : item === "live" ? "Live now" : `${item}s`}</button>)}</div>
          <div className="platform-filter-tabs" aria-label="Platform filters">{PLATFORMS.map((item) => <button key={item} className={platform === item ? "active" : ""} onClick={() => setPlatform(item)} data-testid={`map-platform-${item}-button`}>{item === "all" ? "All platforms" : item === "ios" ? "iOS" : item}</button>)}</div>
        </div>
        <label className="map-search"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="User ID, name, or place" aria-label="Search map and user list" data-testid="map-search-input" /></label>
      </section>

      {loading && !users.length ? <LoadingState label="Loading every production user" /> : error && !users.length ? <ErrorState message={error} onRetry={reload} /> : !API_KEY ? <ErrorState message="Google Maps API key is not configured." onRetry={reload} /> : (
        <>
          <div className="map-workspace">
            <div className="map-canvas" data-testid="map-canvas">
              <div className="google-map-host">
                <APIProvider apiKey={API_KEY} libraries={MAP_LIBRARIES}><OperationsMap points={filteredPoints} selectedKey={selected?.key} onSelect={setSelected} fitNonce={fitNonce} /></APIProvider>
              </div>
              {!filteredPoints.length && <div className="map-no-results" data-testid="map-no-results">No mapped locations match these filters.</div>}
            </div>
            <MapDetailsPanel point={selected} canAct={canAct} onAction={(type, point) => setAction({ type, point })} />
          </div>
          <MapUserTable users={filteredUsers} />
        </>
      )}
      {source === "legacy" && <div className="map-refresh-warning" role="status" data-testid="map-legacy-fallback">Map API is not published yet. Showing temporary compatibility data.</div>}
      {error && users.length > 0 && <div className="map-refresh-warning" role="alert" data-testid="map-refresh-warning">Latest refresh failed: {error}</div>}
      <ActionModal open={!!action} title={modalCopy[0]} description={modalCopy[1]} confirmLabel={modalCopy[2]} tone={action?.type === "unban" ? "primary" : "danger"} fields={action?.type === "ban" ? BAN_FIELDS : NO_FIELDS} onClose={() => setAction(null)} onConfirm={runAction} busy={busy} />
    </div>
  );
}